import { buildFitbWordTrigger, FITB_BLANK_EMPTY, parseFitbWordTrigger } from './fitb.js';
const by = (states, trigger, reachable, cause) => {
    states.push(cause && !reachable ? { trigger, reachable, cause } : { trigger, reachable });
};
const typeable = (w) => w.text.trim() !== '';
const EMPTY = { correctByBlank: [], blanksMissingCorrect: [], uncoveredByBlank: [] };
/**
 * What the catch-all still catches, per blank — the same question graded asks, asked in a mode
 * with no correct word.
 *
 * The participation arms returned the EMPTY lists, which read as "nothing to enumerate" and so
 * rendered the Unreachable chip on a card that in fact catches everything the targeted states do
 * not. Same mistake as Dropdown's blank, one layer down: an empty list is not an absent card.
 *
 * Every typeable word is listed here, not just the non-correct ones, because outside graded no
 * word is correct and so none of them is terminal.
 */
function participationUncovered(blanks, states) {
    const covered = new Set(states.filter(s => s.type === 'targeted').map(s => s.trigger));
    return blanks.map(blank => ({
        blank,
        words: blank.words.filter(w => typeable(w) && !covered.has(buildFitbWordTrigger(blank.id, w.id))),
    }));
}
/** @param blanks the EFFECTIVE blanks, roles already resolved through any matched branch. */
export function fitbReachability(d, blanks) {
    const states = [];
    if (d.gradingMode === 'optional') {
        // Never closes. The answer space is OPEN — a student can always type a word nobody listed —
        // so unlike the single-pick blocks there is no "fully covered" to reach.
        by(states, 'any_response', true);
        // Each blank's own "left empty" target is reachable in optional and nowhere else.
        for (const blank of blanks)
            by(states, buildFitbWordTrigger(blank.id, FITB_BLANK_EMPTY), true);
        for (const t of ['unanswered', 'incomplete_answer', 'correct', 'incorrect', 'any_answer'])
            by(states, t, false, 'mode');
        for (const s of d.feedback.states)
            if (s.type === 'targeted')
                by(states, s.trigger, true);
        return { states, ...EMPTY, uncoveredByBlank: participationUncovered(blanks, d.feedback.states) };
    }
    // With no blanks at all the every-empty test is vacuously true, so unanswered is not merely
    // reachable, it is the only thing that can ever happen.
    by(states, 'unanswered', true);
    // Some-but-not-all-empty needs two blanks to be expressible. With one, the submission is either
    // empty (unanswered) or complete; there is no partial state to land in.
    const partial = blanks.length >= 2;
    by(states, 'incomplete_answer', partial, partial ? undefined : 'needs-two-blanks');
    if (d.gradingMode === 'survey') {
        // any_answer runs after the emptiness gates, so it needs a blank to fill. Targeting every
        // listed word does NOT close it, for the same reason graded's Default Incorrect never closes
        // here: the answer space is open and gibberish falls through.
        by(states, 'any_answer', blanks.length > 0, blanks.length > 0 ? undefined : 'no-blanks');
        for (const t of ['correct', 'incorrect', 'any_response'])
            by(states, t, false, 'mode');
        for (const s of d.feedback.states)
            if (s.type === 'targeted')
                by(states, s.trigger, true);
        return { states, ...EMPTY, uncoveredByBlank: participationUncovered(blanks, d.feedback.states) };
    }
    by(states, 'any_answer', false, 'mode');
    by(states, 'any_response', false, 'mode');
    const correctByBlank = blanks.map(blank => ({ blank, words: blank.words.filter(w => w.role === 'correct' && typeable(w)) }));
    const blanksMissingCorrect = correctByBlank.filter(e => e.words.length === 0).map(e => e.blank);
    const correctCause = blanks.length === 0 ? 'no-blanks'
        : blanksMissingCorrect.length > 0 ? 'blank-missing-correct-word'
            : undefined;
    by(states, 'correct', !correctCause, correctCause);
    const wordById = new Map(blanks.flatMap(b => b.words.map(w => [w.id, w])));
    const covered = new Set();
    for (const s of d.feedback.states) {
        if (s.type !== 'targeted')
            continue;
        const parsed = parseFitbWordTrigger(s.trigger);
        const word = parsed ? wordById.get(parsed.wordId) : undefined;
        // Same two deaths as Keyword's: aimed at a correct word, which returns before targeted runs, or
        // aimed at a blank one, which nothing can type.
        const ok = !!word && word.role !== 'correct' && typeable(word);
        by(states, s.trigger, ok, ok ? undefined : 'blank-missing-correct-word');
        if (ok)
            covered.add(s.trigger);
    }
    const uncoveredByBlank = blanks.map(blank => ({
        blank,
        words: blank.words.filter(w => w.role !== 'correct' && typeable(w) && !covered.has(buildFitbWordTrigger(blank.id, w.id))),
    }));
    // Always reachable once there is a blank to type into, and for Keyword's reason: the answer space
    // is open, so gibberish matches no word, fires no targeted state, and falls through. A blank with
    // no correct word does not make this unreachable — it makes it GUARANTEED.
    by(states, 'incorrect', blanks.length > 0, blanks.length > 0 ? undefined : 'no-blanks');
    return { states, correctByBlank, blanksMissingCorrect, uncoveredByBlank };
}
//# sourceMappingURL=fitb-reachability.js.map